import { Component, EventEmitter, Input, Output, ViewChild, OnInit, signal } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize } from 'rxjs';
import { openConfirmationModal } from 'src/shared/utilities/modal-utilities';
import {
  CheckExistingEducatorQuery,
  CodeTablesClient,
  CreatePersonsCommand,
  EthnicityCodeDto,
  GenderCodeDto,
  GetEducatorSearchQuery,
  RaceCodeDto,
} from 'src/shared/services/api.service';
import { EducatorsClient } from 'src/shared/services/api.service';
import { DateService } from 'src/shared/services/date.service';
import { LoaderService } from 'src/shared/services/loader.service';
import { catchValidationErrors } from 'src/shared/utilities/validator-utilities';

@Component({
  selector: 'cert-educator-create-modal',
  templateUrl: './educator-create-modal.component.html',
  styleUrls: ['./educator-create-modal.component.scss'],
  standalone: false,
})
export class EducatorCreateComponent implements OnInit {
  @Output() toggleSearchComponent = new EventEmitter<GetEducatorSearchQuery>();

  @Input()
  createPersonsCommand: CreatePersonsCommand = {
    ssn: '',
    dateOfBirth: null!,
    firstName: '',
    middleName: '',
    lastName: '',
    generateSSN: false,
    genderCodeId: undefined,
    ethnicityCodeId: undefined,
    raceCodeIds: [],
  };
  @ViewChild('createPersonsForm', { static: true }) form!: NgForm;

  showConfirmationModal = false;
  isConfirmed = false;
  dateOfBirth!: Date | undefined;
  createSSN = false;
  minDate: string = this.dateService.getMinBirthday();
  maxDate: string = this.dateService.getMaxBirthday();
  errorEmitter = new EventEmitter<any>();

  genderCodes = signal<GenderCodeDto[]>([]);
  ethnicityCodes = signal<EthnicityCodeDto[]>([]);
  raceCodes = signal<RaceCodeDto[]>([]);

  constructor(
    private readonly educatorsClient: EducatorsClient,
    private readonly modalService: NgbModal,
    private readonly dateService: DateService,
    private readonly router: Router,
    private readonly activeModalService: NgbActiveModal,
    private readonly codeTablesClient: CodeTablesClient,
    private readonly loaderService: LoaderService,
  ) {}

  ngOnInit() {
    if (this.createPersonsCommand.dateOfBirth) {
      this.dateOfBirth = this.createPersonsCommand.dateOfBirth;
    }

    this.codeTablesClient.getGenderEthnicityRaceCodes().subscribe((x) => {
      this.genderCodes.set(x.genderCodes);
      this.ethnicityCodes.set(x.ethnicityCodes);
      this.raceCodes.set(x.raceCodes);
    });
  }

  onSubmit() {
    if (this.dateOfBirth) {
      this.createPersonsCommand.dateOfBirth = this.dateOfBirth;
    }

    const checkExistingEducatorQuery: CheckExistingEducatorQuery = {
      dateOfBirth: this.createPersonsCommand.dateOfBirth,
      lastName: this.createPersonsCommand.lastName,
    };

    this.educatorsClient.checkExistingEducator(checkExistingEducatorQuery).subscribe({
      next: (result) => {
        if (result) {
          this.openConfirmationModal();
        } else {
          this.createPerson();
        }
      },
    });
  }

  createSearchForCreatedPersonQuery(): GetEducatorSearchQuery {
    const searchForCreatedPersonQuery: GetEducatorSearchQuery = {
      ssn: this.createPersonsCommand.ssn,
      firstName: this.createPersonsCommand.firstName,
      lastName: this.createPersonsCommand.lastName,
      middleName: this.createPersonsCommand.middleName,
      dateOfBirth: this.createPersonsCommand.dateOfBirth,
      pageNumber: 1,
      pageSize: 10,
    };

    return searchForCreatedPersonQuery;
  }

  onCancel() {
    this.toggleSearchComponent.emit(this.createSearchForCreatedPersonQuery());
  }

  createPerson() {
    this.loaderService.loaderOn();
    this.educatorsClient
      .createPersons(this.createPersonsCommand)
      .pipe(
        catchValidationErrors(this.form, this.errorEmitter),
        finalize(() => this.loaderService.loaderOff()),
      )
      .subscribe({
        next: () => {
          this.toggleSearchComponent.emit(this.createSearchForCreatedPersonQuery());
        },
        complete: () => this.loaderService.loaderOff(),
      });
  }

  goToEducatorManagement(personId: number) {
    this.activeModalService.close();
    this.router.navigateByUrl(`cert/educators/${personId}`);
  }

  openConfirmationModal() {
    openConfirmationModal(
      this.modalService,
      'Similar Educator Found',
      'An educator with the same Last Name and Date of Birth was found. Are you sure you want to create a new Educator?',
      () => this.createPerson(),
      undefined,
      () => this.onCancel(),
    );
  }

  clearSSN() {
    this.createPersonsCommand.ssn = '';
  }
}
